import DateSelector from 'components/inputs/dateSelector';
import Page from 'components/menu/page.component';
import { KEY } from 'constants/local_storage_key.constant';
import { Planning } from 'interfaces/planning.interface';
import { ModifyTask, PostTask, TaskFull } from 'interfaces/task.interface';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlanningService } from 'services/planning.service';
import { TaskService } from 'services/task.service';
import { RootState, dayjs, ShortUser, Equipment, CommonEquipmentService, usePersistantState, UserIcon, LocationIcon } from '@umanforge/common';
import { UserService } from 'services/users.service';
import { ChronogramComponent } from 'features/chronogram/components/chronogram.component';
import TabsSlider from '@common_components/buttons/TabsSlider.component';
import { TabItem } from '@common_components/buttons/Tabs.component';
import { RoleTaskFullIds } from 'interfaces/roleTask.interface';
import { SettingService } from 'services/setting.service';
import { SectorSettings } from 'interfaces/setting.interface';
import { useTranslation } from 'react-i18next';
import { RoleTaskService } from 'services/roleTask.service';
import { ChronogramLoading } from 'features/chronogram/components/chronogramLoading.component';
import { TaskEquipmentService } from 'services/task_equipment.service';
import { TaskEquipment } from 'interfaces/task_equipment.interface';
import { Location } from 'interfaces/location.interface';
import { LocationService } from 'services/location.service';
import { TaskIcon } from 'components/icons/task.icon';
import TimeGridSelectorComponent from 'features/timegrid_selector/components/timegrid_selector.component';
import { TimeGrid } from 'interfaces/timegrid.interface';
import { DEFAULT_VALUES } from 'constants/calendar.constants';
import { TimeGridRecurrenceService } from 'services/timeGridRecurrence.service';
import { TaskUserUtils } from 'utils/taskUser.utils';
import { ViewOptionsMenu } from 'components/menu/viewOptionsMenu.component';
import WarningComponent from 'features/warning/components/warning.component';

function ChronogramPage() {
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();
  const me = useSelector( ( state: RootState ) => {return state.user.value;} );
  const { t } = useTranslation();

  const { tabs } = useMemo( () => {
    return {
      tabs: [
        { id: 'distribution', name: t( 'translation:distribution' ), icon: <UserIcon className='' /> },
        { id: 'task', name: t( 'translation:task' ), icon: <TaskIcon className='h-8 stroke-[0.2]' /> },
        { id: 'location', name: t( 'translation:location' ), icon: <LocationIcon className='' /> },
        { id: 'equipment', name: t( 'common:equipment' ) },
      ]
    };
  }, [ t ] );

  const [ isAdmin, setIsAdmin ] = useState( false );
  const [ showOnlyMyTasks, setShowOnlyMyTasks ] = useState<boolean>( false );
  const [ highlightMyTasks, setHighlightMyTasks ] = useState<boolean>( false );

  const [ currentDayISO, setCurrentDayISO ] = usePersistantState< string | undefined >( KEY.SELECTED_DAY, undefined );
  const [ currentDay, setCurrentDay ] = useState<dayjs.Dayjs>( dayjs( currentDayISO ) );

  const [ selectedViewPersistent, setSelectedViewPersistent ] = usePersistantState< string | undefined >( KEY.SELECTED_VIEW, 'week' );
  const [ isWeek, setIsWeek ] = useState<boolean>( selectedViewPersistent == 'week' );

  const [ planning, setPlanning ] = useState<Planning>(  );
  const [ tasks, setTasks ] = useState<TaskFull[] | undefined>(  );

  const [ roleTasks, setRolesTasks ] = useState<RoleTaskFullIds[]>( [] );
  const [ users, setUsers ] = useState<ShortUser[]>( [] );

  const [ equipments, setEquipments ] = useState<Equipment[]>( [] );
  const [ taskEquipments, setTaskEquipments ] = useState<TaskEquipment[]>( [] );
  const [ locations, setLocations ] = useState<Location[]>( [] );

  const [ settings, setSettings ] = useState<SectorSettings>( );

  const [ openTaskModal, setOpenTaskModal ] = useState<string | undefined>();

  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<string | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const [ selectedTab, setSelectedTab ] = useState<TabItem>( tabs[ 0 ] );

  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ userTimeGrids, setUserTimeGrids ] = useState<Map<string, TimeGrid>>( new Map() );

  const [ selectedTimeGrid, setSelectedTimeGrid ] = useState<TimeGrid>( {
    id: 'virtual_time_grid',
    name: '',
    idSector: selectedSectorId,
    timeStart: `1970-1-0 ${ DEFAULT_VALUES.HOURSTART }:00`,
    timeEnd: `1970-1-0 ${ DEFAULT_VALUES.HOUREND }:00`,
    disabled: false,
    breaktimes: [],
  } as TimeGrid );



  const fetchTimeGridRecurrence = async () => {
    if ( selectedSectorId && !isWeek ) {
      const dateStart = currentDay.startOf( 'day' ).toISOString();
      const dateEnd = currentDay.endOf( 'day' ).toISOString();
      const _timegrids = await TimeGridRecurrenceService.getTimeGridRecurrencesBySectorIdAndDates( selectedSectorId, dateStart, dateEnd, true, token, dispatch );
      if ( _timegrids ) {
        const timeGridOfDay = _timegrids.filter( tg => { return dayjs( tg.date ).format( 'YYYY-MM-DD' ) == currentDay.format( 'YYYY-MM-DD' ); } );
        const tgrByUser = new Map<string, TimeGrid>();
        if ( timeGridOfDay.length > 0 ) {
          timeGridOfDay.forEach( tgr => {
            tgrByUser.set( tgr.idUser, tgr.timeGrid );
          } );
        }
        setUserTimeGrids( tgrByUser );
      }
      else {
        setUserTimeGrids( new Map() );
      }
    }
  };

  useEffect( () => {
    fetchTimeGridRecurrence();
  }, [ settings, currentDay ] );


  useEffect( () => {
    if ( openTaskModal != null ) {
      setOpenTaskModal( undefined );
    }
  }, [ openTaskModal ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshTasks( planning?.id );
    }
  }, [ currentDay, isWeek, isFirstRender, showOnlyMyTasks ] );


  useEffect( () => {
    if ( currentDayISO != null ) {
      setCurrentDay( dayjs( currentDayISO ) );
    }
  }, [ currentDayISO ] );

  useEffect( () => {
    if ( selectedViewPersistent != null ) {
      setIsWeek( selectedViewPersistent == 'week' );
    }
  }, [ selectedViewPersistent ] );

  useEffect( () => {
    setTimeout( () => {
      setIsFirstRender( false );
    }, 800 );
  }, [] );


  useEffect( () => {
    if ( !isFirstRender ) {
      fetchSectorData();
    }
  }, [ isFirstRender ] );


  useEffect( () => {
    if ( !isFirstRender ) {
      handleSectorChange();
    }
  }, [ selectedSectorId ] );

  //--------------- Functions ---------------\\

  const refreshTasks = async ( planningId: string | undefined ) => {
    if ( planningId ) {
      const dates = !isWeek ?
        {
          dateStart: currentDay.startOf( 'day' ).toISOString(),
          dateEnd: currentDay.endOf( 'day' ).toISOString(),
        } :
        {
          dateStart: currentDay.startOf( 'day' ).toISOString(),
          dateEnd: currentDay.add( 6, 'day' ).endOf( 'day' ).toISOString()
        };

      const _tasks = await TaskService.getTasksByPlanningIdAndDate( planningId, dates, token, dispatch );
      if ( _tasks.length > 0 ) {
        try {
          const [ _roleTasks, _taskEquipments ] = await Promise.all( [
            RoleTaskService.getRoleTasksFullByTaskIds( _tasks.map( task => { return task.id; } ), token, dispatch  ),
            TaskEquipmentService.getTaskEquipmentByTasksId( _tasks.map( task => { return task.id; } ), token, dispatch )
          ] );

          if ( !showOnlyMyTasks || !me ) {
            setTasks( _tasks );
          }
          else {
            setTasks( TaskUserUtils.filterTasksByUserWithRTIds( _tasks, me.id, _roleTasks ) );
          }
          setRolesTasks( _roleTasks );
          setTaskEquipments( _taskEquipments );
        }
        catch ( error ) {
          console.error( 'error fetch data', error );
          setTasks( [] );
          setRolesTasks( [] );
          setTaskEquipments( [] );
        }
      }
      else {
        setTasks( [] );
        setRolesTasks( [] );
        setTaskEquipments( [] );
      }
    }
  };

  const fetchSectorData = async () => {
    if ( selectedSectorId ) {
      try {
        const [
          _users,
          _planning,
          _settings,
          _equipments,
          _locations
        ] = await Promise.all( [
          UserService.getUsersOfSector( selectedSectorId, token, dispatch ),
          PlanningService.getPlanningBySectorId( selectedSectorId, token, dispatch ),
          SettingService.getSectorSettingsBySector( selectedSectorId, token, dispatch ),
          CommonEquipmentService.getAllowedEquipmentsBySectorsId( [ selectedSectorId ], token, dispatch ),
          LocationService.getLocationBySector( selectedSectorId, token, dispatch ),
        ] );

        setUsers( _users );
        setPlanning( _planning );
        setSettings( _settings );
        setEquipments( _equipments );
        setLocations( _locations );

        if ( _planning.id != null ) {
          await refreshTasks( _planning.id );
        }
      }
      catch ( error ) {
        console.error( 'error fetch data', error );
      }
    }
  };

  const handleSectorChange = async () => {
    await fetchSectorData();
  };


  //--------------- Events ---------------\\
  const onSectorsChange = ( sectorId: string | undefined ) => {
    if ( !isFirstRender ) {
      setSelectedSectorId( sectorId );
    }
  };

  const onTabChange = ( index: number ) => {
    setSelectedTab( tabs[ index ] );
  };

  const onViewChange = ( checked: boolean ) => {
    setSelectedViewPersistent( !checked ? 'week' : 'day' );
    setTasks( [] );
    if ( !checked ) {
      setCurrentDayISO( currentDay.startOf( 'week' ).toISOString() );
    }
    // si c'est sur la meme semaine, aller au jour d'aujoud'hui
    else if ( currentDay.startOf( 'week' ).diff( dayjs().startOf( 'week' ), 'day' ) == 0 ) {
      setCurrentDayISO( dayjs().startOf( 'day' ).toISOString() );
    }
  };

  const goToDay = ( day: dayjs.Dayjs ) => {
    setSelectedViewPersistent( 'day' );
    setTasks( [] );
    setCurrentDayISO( day.startOf( 'day' ).toISOString() );
  };


  const onTaskCreate = async ( task: PostTask ): Promise<string> => {
    const taskPost: PostTask = {
      idPlanning: task.idPlanning,
      idLocation: task.idLocation,
      idTaskAction: task.idTaskAction,
      status: task.status,
      debord: task.debord,
      nbSmallPackage: task.nbSmallPackage,
      nbBigPackage: task.nbBigPackage,
      dateStart: task.dateStart,
      dateEnd: task.dateEnd,
      idRecurrence: task.idRecurrence,
    };

    const taskId = ( await TaskService.postTask( taskPost, token, dispatch ) ).id;
    refreshTasks( planning?.id );

    return taskId;
  };

  const onTaskUpdate = async ( task: ModifyTask ) => {
    const taskModify: ModifyTask = {
      idLocation: task.idLocation,
      idTaskAction: task.idTaskAction,
      status: task.status,
      debord: task.debord,
      nbSmallPackage: task.nbSmallPackage,
      nbBigPackage: task.nbBigPackage,
      dateStart: task.dateStart,
      dateEnd: task.dateEnd,
      idRecurrence: task.idRecurrence,
      id: task.id
    };

    let passed = false;
    await TaskService.putTask( task.id, taskModify, token, dispatch ).then( () => {
      refreshTasks( planning?.id );
      passed = true;
    } );

    return passed;
  };

  const onTaskDelete = ( taskId: string ) => {
    TaskService.deleteTask( taskId, token, dispatch ).then( () => {
      refreshTasks( planning?.id );
    } );
  };

  return (
    <Page
      footer={false}
      isAdminRequired={ false }
      onSectorChange={onSectorsChange}
      setIsAdmin={ setIsAdmin }
      headerNavigateBarChildren={
        <div className='grid grid-flow-col grid-cols-2 self-justify-end w-full'>
          <div className='flex items-center justify-center gap-x-2 w-full'>
            <div className='w-fit'>
              <DateSelector
                setCurrentDay={ ( day: dayjs.Dayjs ) => {
                  if ( !isFirstRender ) {
                    setCurrentDayISO( day.toISOString() );
                  }
                } }
                currentDay={ currentDay }
                isWeek={ isWeek }
                areWeekEnd={ ( settings?.showWeekend?.value ?? 0 ) == 1 }
              />
            </div>
            <div className="w-fit mt-1">
              <ViewOptionsMenu
                isDayView={!isWeek}
                highlightMyTasks={highlightMyTasks}
                showOnlyMyTasks={showOnlyMyTasks}
                onViewChange={onViewChange}
                onHighlightMyTasks={setHighlightMyTasks}
                onShowOnlyMyTasks={setShowOnlyMyTasks}
              />
            </div>
          </div>

          <div className='flex items-center justify-center gap-x-2 w-full'>
            { planning &&
              <WarningComponent
                planningId={planning.id}
                onOpenWarning={ setOpenTaskModal}
                className='z-20'
                update={ tasks?.length }
              />
            }

            { selectedSectorId &&
              <TimeGridSelectorComponent
                sectorId={ selectedSectorId }
                onTimeGridChange={ setSelectedTimeGrid }
                className='z-20'
                canBeEmpty
              />
            }
          </div>
        </div>
      }
    >
      <>
        <TabsSlider
          sectors={ tabs }
          onSectorClick={onTabChange}
          selectedIndex={ 0 }
        />

        <div className="p-0 mt-1 -mr-4 h-[86vh]">
          { ( selectedSectorId && planning && tasks && settings && roleTasks && settings ) ?
            <>
              <Fragment>
                <ChronogramComponent
                  planning={planning}
                  tasks={tasks}
                  users={users}
                  roleTasks={roleTasks}
                  equipments={equipments}
                  taskEquipments={taskEquipments}
                  currentDate={currentDay}
                  selectedTab={ selectedTab }
                  settings={settings}
                  isWeek={isWeek}
                  locations={locations}
                  isAdmin={isAdmin}

                  onTaskCreate={onTaskCreate}
                  onUpdate={onTaskUpdate}
                  onDeleteTask={onTaskDelete}
                  refreshTask={ () => { refreshTasks( planning.id ); }}
                  goToDay={goToDay}
                  timegrid={ selectedTimeGrid }
                  timeGridUsers={userTimeGrids}
                  userToHighlight={highlightMyTasks ? me?.id : undefined }
                  openTaskModal={ openTaskModal }
                />
              </Fragment>
            </> :
            <ChronogramLoading/>
          }
        </div>
      </>
    </Page>
  );
}

export default ChronogramPage;